import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, X, Edit, Trash2, Mail } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { Pagination } from '../../../shared/components/Pagination';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { ClienteForm } from '../components/ClienteForm';
import { ClienteDuplicadoModal } from '../components/ClienteDuplicadoModal';
import { formatCNPJ, formatCPF } from '../../../shared/utils/validators';
import { useClientes, useCriarCliente, useExcluirCliente } from '../hooks';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import {
  ClienteComVinculosError,
  ClienteDuplicadoError,
  CreateClientePayload,
  ExistingClienteRef,
} from '../api';
import type { ClienteListItem, SituacaoCadastral } from '../api';

const DEFAULT_PAGE_SIZE = 25;

const SITUACAO_LABEL: Record<SituacaoCadastral, string> = {
  em_cadastro: 'Em Cadastro',
  ativo: 'Ativo',
  inativo: 'Inativo',
  suspenso: 'Suspenso',
  bloqueado: 'Bloqueado',
  encerrado: 'Encerrado',
};

const SITUACAO_BADGE: Record<SituacaoCadastral, string> = {
  em_cadastro: 'bg-yellow-100 text-yellow-800',
  ativo: 'bg-green-100 text-green-800',
  inativo: 'bg-gray-100 text-gray-700',
  suspenso: 'bg-orange-100 text-orange-800',
  bloqueado: 'bg-red-100 text-red-800',
  encerrado: 'bg-gray-200 text-gray-800',
};

const ClientesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [duplicado, setDuplicado] = useState<ExistingClienteRef | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Debounce da busca (300ms) — evita bater no backend a cada tecla.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset da paginação quando a busca muda (senão pode cair em página vazia).
  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch]);

  const { data, isLoading, isError, error } = useClientes({
    limit,
    offset,
    search: debouncedSearch || undefined,
  });
  const criar = useCriarCliente();
  const excluir = useExcluirCliente();

  const items: ClienteListItem[] = data?.data ?? [];
  const count = data?.pagination?.count ?? 0;

  const handleCreate = async (payload: CreateClientePayload) => {
    setSubmitError(null);
    try {
      const created = await criar.mutateAsync(payload);
      setShowModal(false);
      // Vai direto pra ficha do cliente recém-criado — o usuário quase
      // sempre precisa complementar cadastro (endereço, documentos etc.)
      // antes de conseguir ativar. A toast de sucesso é exibida lá.
      navigate(`/clientes/${created.id}`, {
        state: { successMessage: `Cliente ${created.code} cadastrado com sucesso!` },
      });
    } catch (err) {
      if (err instanceof ClienteDuplicadoError) {
        setDuplicado(err.existingClient);
      } else {
        setSubmitError(getFriendlyErrorMessage(err, 'Erro ao cadastrar cliente.'));
      }
    }
  };

  const [confirmDelete, setConfirmDelete] = useState<ClienteListItem | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const closeModal = () => {
    setShowModal(false);
    setSubmitError(null);
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    const cliente = confirmDelete;
    setDeleteError(null);
    try {
      await excluir.mutateAsync(cliente.id);
      setSuccessMessage(`Cliente ${cliente.code} excluído.`);
      setTimeout(() => setSuccessMessage(null), 5000);
      setConfirmDelete(null);
    } catch (err) {
      if (err instanceof ClienteComVinculosError) {
        const lista = Object.entries(err.vinculos)
          .filter(([, n]) => n > 0)
          .map(([k, n]) => `${k}: ${n}`)
          .join(', ');
        setDeleteError(
          `Não foi possível excluir: cliente possui vínculos ativos (${lista}). Use a ação "Inativar" ou "Encerrar" no detalhe do cliente.`,
        );
        return;
      }
      setDeleteError(getFriendlyErrorMessage(err, 'Falha ao excluir cliente.'));
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Novo Cliente
        </button>
      </div>

      {successMessage && (
        <div className="p-4 rounded-lg border bg-green-50 border-green-200">
          <p className="text-sm text-green-600">{successMessage}</p>
        </div>
      )}

      <div className="max-w-md mb-4">
        <TextInput
          label="Buscar clientes"
          icon={<Search className="w-5 h-5" />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div>Carregando...</div>
      ) : isError ? (
        <div className="text-red-600">Erro: {(error as Error)?.message}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Razão Social</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CNPJ / CPF</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Situação</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Segmento</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">
                    Nenhum cliente encontrado.
                  </td>
                </tr>
              )}
              {items.map((c) => {
                const doc = c.tipoPessoa === 'PJ' ? formatCNPJ(c.documento) : formatCPF(c.documento);
                return (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 font-mono">{c.code}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{c.razaoSocial}</div>
                      {c.nomeFantasia && <div className="text-xs text-gray-500">{c.nomeFantasia}</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900 font-mono">
                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                        {doc}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {c.tipoPessoa}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${SITUACAO_BADGE[c.situacaoCadastral]}`}>
                        {SITUACAO_LABEL[c.situacaoCadastral]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {c.segmentoMercado}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        <a
                          href={`/clientes/${c.id}`}
                          className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded transition-colors"
                          title="Editar cliente"
                          aria-label={`Editar cliente ${c.razaoSocial}`}
                        >
                          <Edit className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => { setDeleteError(null); setConfirmDelete(c); }}
                          disabled={excluir.isPending}
                          className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          title="Excluir cliente (só se sem vínculos ativos — RN-COM-004)"
                          aria-label={`Excluir cliente ${c.razaoSocial}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <Pagination
            offset={offset}
            limit={limit}
            count={count}
            itemLabel="cliente"
            onChange={({ offset: o, limit: l }) => { setOffset(o); setLimit(l); }}
          />
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Cadastrar Novo Cliente</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <ClienteForm
                mode="create"
                submitting={criar.isPending}
                submitError={submitError}
                onSubmit={handleCreate}
                onCancel={closeModal}
              />
            </div>
          </div>
        </div>
      )}

      {duplicado && (
        <ClienteDuplicadoModal existingClient={duplicado} onClose={() => setDuplicado(null)} />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir cliente?"
        message={
          confirmDelete
            ? `${confirmDelete.code} — ${confirmDelete.razaoSocial}\n\n${
                deleteError
                  ? deleteError
                  : 'Essa ação só é permitida se o cliente não tiver vínculos ativos (contratos, projetos, ativos, custodiários). Caso contrário, use "Inativar" ou "Encerrar" no detalhe.'
              }`
            : ''
        }
        confirmLabel="Excluir"
        danger
        busy={excluir.isPending}
        onConfirm={performDelete}
        onCancel={() => { setConfirmDelete(null); setDeleteError(null); }}
      />
    </div>
  );
};

export default ClientesPage;
